<template>
  <div class="chat-input-container">
    <div class="w-full bg-white py-2 px-4" :class="{ 'py-4': imageUrl }">
      <div v-if="imageUrl" class="relative max-h-20 max-w-20 bg-gray-50">
        <div class="absolute -top-3.5 -right-3.5">
          <CGButton variant="outline" color="accent" shape="circle" class="bg-white" small @click="removeItem">
            <Icon name="close" :size="16" />
          </CGButton>
        </div>
        <img :src="imageUrl" :alt="imageFile?.name || ''" class="max-h-20 w-auto m-auto" />
      </div>
      <CGTextarea
        v-model="newMessage"
        :rows="2"
        class="w-full my-2"
        :placeholder="$t('ChatBot.InputPlaceholder')"
        :errored="chatInputError"
        @keydown.enter.exact.prevent="onEnter"
      />
    </div>
    <div class="flex self-end mt-1 space-x-2">
      <Tooltip :title="$t('ChatBot.SearchTooltip')" mode="hover" placement="top">
        <div class="flex items-center text-xs pl-4">
          <span class="search-documents">{{ $t('ChatBot.Search') }}</span>
          <Toggle v-model="searchMessage" class="mx-2 flex items-center" />
        </div>
      </Tooltip>
      
      <CGButton ref="chatImage" variant="outline" @click="showUploadModal">
        <Icon name="attachment" :size="16" :aria-hidden="true" />
      </CGButton>
      <slot name="buttons" />
    </div>
    <FileUploadModal
      v-if="showModal"
      ref="fileUploadModal"
      :trigger="$refs.chatImage"
      file-label="chatImage"
      :max-size="maxFileSize"
      :accepted-formats="['.JPG', '.JPEG', '.PNG']"
      @submit="submitFileUpload"
    />
  </div>
</template>

<script>
import { defineComponent } from 'vue'

export default defineComponent({
  props: {
    message: {
      type: String,
      default: undefined,
    },
    imageUrl: {
      type: String,
      default: undefined,
    },
    imageFile: {
      type: [File, Object],
      default: undefined,
    },
    errored: {
      type: Boolean,
      default: undefined,
    },
    isTyping: {
      type: Boolean,
      default: undefined,
    },
  },
  emits: ['send', 'update:imageFile', 'update:imageUrl', 'update:message', 'update:search'],
  data() {
    return {
      newMessage: this.message,
      searchMessage: false,
      maxFileSize: '1 MB',
      showModal: false
    }
  },
  computed: {
    chatInputError() {
      return this.errored && (this.newMessage && this.newMessage.trim() == '')
    },
  },
  watch: {
    message(newVal) {
      this.newMessage = newVal
    },
    newMessage() {
      this.$emit('update:message', this.newMessage)
    },
    searchMessage(newVal) {
      this.$emit('update:search', newVal)
    }
  },
  methods: {
    showUploadModal() {
      this.showModal = true
      this.$nextTick(() => {
        if (this.$refs.fileUploadModal) {
          this.$refs.fileUploadModal.showUploadModal(this.showModal)
        }
      })
    },
    submitFileUpload(files) {
      const uploadedFile = files.fileList[0]
      this.$emit('update:imageFile', uploadedFile)
      let imageUrl = URL.createObjectURL(uploadedFile)
      this.$emit('update:imageUrl', imageUrl)
    },
    removeItem() {
      this.$emit('update:imageUrl', '')
      this.$emit('update:imageFile', null)
    },
    onEnter(e) {
      if (this.isTyping || e?.enterKey || e?.shiftKey) return;
      this.$emit('send');
    },
  },
})
</script>

<style scoped>
.chat-input-container {
  @apply flex flex-col items-center w-full;
}

.chat-send-button {
  @apply p-2.5 ml-2.5 text-white rounded bg-custom-primary;
}

.chat-box:not(.expanded) .search-documents {
  max-width: 108px;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: normal;
  line-height: 1.3;
}
</style>
